/* eslint-disable react/prop-types */
import { formatPhoneNumber } from 'util/formatPhoneNumber';
import { Box } from '@mui/material';
import {
  Button,
  FormErrors,
  Modal,
  ModalProps,
  TextInput,
} from 'components/ui';
import { UpdateMobileNumberForm, updateMobileNumberFormSchema } from 'forms';
import { useAppForm, useProfile } from 'hooks';
import React, { ReactElement, useEffect, useRef, useState } from 'react';
import { useProfileApi } from 'services/api';
import { throttle } from 'lodash';
import { useSnackbar } from 'notistack';
import { Controller } from 'react-hook-form';

export interface UpdateMobileNumberModalProps
  extends Pick<ModalProps, 'open' | 'onClose'> {}

export const UpdateMobileNumberModal = ({
  open,
  onClose,
}: UpdateMobileNumberModalProps): ReactElement => {
  const { profile } = useProfile({ revalidateOnMount: true });

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors, touchedFields: touched },
  } = useAppForm(updateMobileNumberFormSchema);

  const [isSubmitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) setSubmitting(false);

    if (!profile) return;

    reset({
      mobileNumber: profile.mobileNumber,
    });
  }, [profile, open]);

  const profileApi = useProfileApi();

  const { enqueueSnackbar } = useSnackbar();

  const onSubmit = throttle(
    async (data: UpdateMobileNumberForm) => {
      setSubmitting(true);
      const result = await profileApi.updateMobileNumber(data.mobileNumber);

      if (result.success) return onClose?.();

      setSubmitting(false);
      enqueueSnackbar(result.error.message, { variant: 'error' });
    },
    200,
    { leading: true },
  );

  const ref = useRef<HTMLInputElement | null>(null);

  return (
    <Modal title="Update Your Mobile Number" open={open} onClose={onClose}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Box sx={{ width: 600, maxWidth: '80vw' }} display="flex">
          <Box width={300} mr={20}>
            <Controller
              name="mobileNumber"
              control={control}
              defaultValue={profile?.mobileNumber ?? ''}
              render={({ field: { value, onChange, ref: _ref, ...field } }) => {
                return (
                  <TextInput
                    label="Mobile Number"
                    ref={ref}
                    value={
                      ref.current?.selectionStart === undefined ||
                      (ref.current?.selectionStart ?? 0) >= value.length
                        ? formatPhoneNumber(
                            value?.replace(/\D/g, '').slice(0, 10),
                          )
                        : `${value}`
                    }
                    onChange={e => {
                      const value = e.target.value;
                      const transformedValue =
                        (e.target?.selectionStart ?? -1) >= value.length
                          ? value.replace(/\D/g, '').slice(0, 10)
                          : value;
                      onChange(transformedValue);
                    }}
                    hasError={touched.mobileNumber && !!errors.mobileNumber}
                    {...field}
                  />
                );
              }}
            />
            <FormErrors>{errors.mobileNumber?.message}</FormErrors>
          </Box>
        </Box>

        <Box mt={30} display="flex">
          <Box mr={30}>
            <Button minWidth={100} inverted onClick={onClose}>
              Cancel
            </Button>
          </Box>
          <Button
            minWidth={100}
            type="submit"
            variant="vibrant"
            disabled={isSubmitting}
          >
            Save
          </Button>
        </Box>
      </form>
    </Modal>
  );
};
